import React, { useState, useRef, useEffect } from 'react';
import { Mic, Send, Copy, Check, Code } from 'lucide-react';

// --- Helper: Sanitize HTML ---
const escapeHtml = (unsafe) => {
    if (!unsafe) return '';
    return unsafe
         .replace(/&/g, "&amp;")
         .replace(/</g, "&lt;")
         .replace(/>/g, "&gt;")
         .replace(/"/g, "&quot;")
         .replace(/'/g, "&#039;");
};

// --- Helper: Syntax Highlighting ---
const simpleSyntaxHighlight = (code) => {
    const safeCode = escapeHtml(code);
    return safeCode
        .replace(/\b(const|let|var|function|return|import|from|export|default|async|await|new|if|else|for|while|switch|case|break|try|catch|finally|require|module|exports|app|get|post|listen)\b/g, '<span class="text-pink-400">$1</span>') // Keywords
        .replace(/(\'|\"|\`)(.*?)\1/g, '<span class="text-green-300">$1$2$1</span>') // Strings
        .replace(/(\/\/.*|\/\*[\s\S]*?\*\/)/g, '<span class="text-gray-500">$1</span>') // Comments
        .replace(/\b(\d+)\b/g, '<span class="text-yellow-400">$1</span>') // Numbers
        .replace(/([\(\)\{\}\[\]])/g, '<span class="text-cyan-400">$1</span>'); // Brackets
};

// --- CodeBlock Component ---
const CodeBlock = ({ code, language }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
        document.body.removeChild(textArea);
    };

    return (
        <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-lg my-2 text-sm font-mono overflow-hidden">
            <div className="flex justify-between items-center px-4 py-2 bg-gray-800/50">
                <div className="flex items-center gap-2 text-gray-400 text-xs">
                    <Code size={14} />
                    <span>{language || 'Code'}</span>
                </div>
                <button
                    onClick={handleCopy}
                    className="flex items-center gap-2 text-xs text-gray-300 hover:text-white transition-colors"
                >
                    {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                    {copied ? 'Copied!' : 'Copy'}
                </button>
            </div>
            <pre className="p-4 overflow-x-auto">
                <code dangerouslySetInnerHTML={{ __html: simpleSyntaxHighlight(code) }} />
            </pre>
        </div>
    );
};


// --- ChatMessage Component ---
const ChatMessage = ({ role, text }) => {
    const isUser = role === 'user';
    const codeBlockRegex = /^```([a-z]+)?\n([\s\S]*?)\n```$/;
    const parts = text.split(/(```(?:[a-z]+)?\n[\s\S]*?\n```)/g).filter(Boolean);

    return (
        <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in-up`}>
            <div
                className={`max-w-xl lg:max-w-2xl px-5 py-3 rounded-2xl ${
                    isUser
                        ? 'bg-blue-600/80 rounded-br-lg'
                        : 'bg-gray-700/60 rounded-bl-lg'
                }`}
            >
                <strong className={`text-sm font-bold ${isUser ? 'text-white' : 'text-teal-300'}`}>
                    {isUser ? 'You' : 'Jarvis'}
                </strong>
                {parts.map((part, index) => {
                    const match = part.match(codeBlockRegex);
                    if (match) {
                        const language = match[1] || null;
                        const code = match[2].trim();
                        return <CodeBlock key={index} code={code} language={language} />;
                    }
                    const formattedPart = escapeHtml(part)
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>');
                    return (
                         <p key={index} className="text-white whitespace-pre-wrap leading-relaxed break-words" dangerouslySetInnerHTML={{ __html: formattedPart }} />
                    );
                })}
            </div>
        </div>
    );
};


// --- JarvisAssistant Main Component ---
const JarvisAssistant = () => {
    const [conversation, setConversation] = useState([
        { role: 'model', text: "Hello! I'm Jarvis. I can assist with questions, generate code, and more. How can I help you today?" }
    ]);
    const [status, setStatus] = useState('idle'); // 'idle', 'listening', 'processing'
    const [isMuted, setIsMuted] = useState(false);
    const [textInput, setTextInput] = useState('');
    
    const conversationRef = useRef(null);
    const recognitionRef = useRef(null);
    
    // ** THE FIX **: Use a ref to hold the current status.
    // This gives the speech recognition event handlers (onend, onerror)
    // access to the LATEST state, avoiding issues with stale closures.
    const statusRef = useRef(status);
    useEffect(() => {
        statusRef.current = status;
    }, [status]);

    // --- Speech Recognition Setup (runs only once) ---
    useEffect(() => {
        if (!('webkitSpeechRecognition' in window)) {
            console.warn("Speech Recognition not supported in this browser.");
            return;
        }

        const recognition = new window.webkitSpeechRecognition();
        recognition.continuous = false;
        recognition.lang = 'en-US';
        recognition.interimResults = false;

        recognition.onresult = (event) => {
            const spokenText = event.results[0][0].transcript;
            handleUserInput(spokenText);
        };

        recognition.onend = () => {
            // Use the ref to check the most up-to-date status.
            // Only reset to 'idle' if we were 'listening'. If we are 'processing',
            // it means onresult fired and we should not interfere.
            if (statusRef.current === 'listening') {
                setStatus('idle');
            }
        };

        recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error);
            // Always reset on error.
            if (statusRef.current === 'listening') {
                setStatus('idle');
            }
        };
        
        recognitionRef.current = recognition;
    }, []); // Empty dependency array ensures this runs only once.

    // --- Text-to-Speech Function ---
    const speak = (text) => {
        if (isMuted || !text) return;
        const cleanText = text.replace(/```[\s\S]*?```/g, 'I have generated a code block for you.');
        const utterance = new SpeechSynthesisUtterance(cleanText);
        utterance.rate = 1.3;
        utterance.pitch = 1.1;
        speechSynthesis.speak(utterance);
    };

    // --- Actions ---
    const startListening = () => {
        if (status !== 'idle' || !recognitionRef.current) return;
        try {
            setStatus('listening');
            recognitionRef.current.start();
        } catch(e) {
            console.error("Error starting speech recognition:", e);
            setStatus('idle'); // Reset state if start fails
        }
    };

    const handleUserInput = async (message) => {
        if (!message.trim()) {
            setStatus('idle'); // Reset if input is empty
            return;
        }

        setStatus('processing'); // Set to processing immediately
        const newUserMessage = { role: 'user', text: message };
        
        // Use functional update to get the latest conversation
        setConversation(prev => [...prev, newUserMessage]);

        const apiContents = [...conversation, newUserMessage].map(msg => ({
            role: msg.role,
            parts: [{ text: msg.text }],
        }));

        const aiReply = await sendToGemini(apiContents);
        const newModelMessage = { role: 'model', text: aiReply };

        setConversation(prev => [...prev, newModelMessage]);
        setStatus('idle');
        speak(aiReply);
    };

    const handleTextInputSubmit = () => {
        if (textInput.trim() === '') return;
        handleUserInput(textInput);
        setTextInput('');
    };
    
    // --- Gemini API Call ---
    const sendToGemini = async (contents) => {
        const API_KEY = 'AIzaSyDVcqb7Ky2DXq7Q3OWqEg82-ljuRzwM8vE'; 
        const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${API_KEY}`;
        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ contents }),
            });
            if (!res.ok) {
                 const errorBody = await res.json();
                 console.error('API Error Response:', errorBody);
                 throw new Error(`API request failed with status ${res.status}`);
            }
            const data = await res.json();
            return data?.candidates?.[0]?.content?.parts?.[0]?.text || 'Sorry, I couldn’t respond.';
        } catch (error) {
            console.error('Error calling Gemini API:', error);
            return 'Sorry, there was an error processing your request. Please check the console for details.';
        }
    };

    // --- Auto-scroll Effect ---
    useEffect(() => {
        if (conversationRef.current) {
            conversationRef.current.scrollTo({
                top: conversationRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [conversation]);

    // --- Status Indicator ---
    const StatusIndicator = () => {
        if (status === 'idle') return null;
        return (
            <div className="flex items-center justify-start gap-3 animate-pulse pl-4">
                <div className="w-2 h-2 bg-teal-300 rounded-full animate-bounce-short"></div>
                <div className="w-2 h-2 bg-teal-300 rounded-full animate-bounce-short [animation-delay:0.1s]"></div>
                <div className="w-2 h-2 bg-teal-300 rounded-full animate-bounce-short [animation-delay:0.2s]"></div>
                <span className="text-sm font-medium text-teal-300">
                    {status === 'listening' ? 'Listening...' : 'Jarvis is thinking...'}
                </span>
            </div>
        );
    };

    return (
        <div className="bg-gray-900 text-white h-screen w-full flex flex-col font-sans">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900/50 -z-10"></div>
            
            <header className="p-4 border-b border-gray-700/50 shadow-lg bg-gray-900/50 backdrop-blur-sm flex-shrink-0">
                <h1 className="text-2xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-blue-500">
                    Jarvis AI Assistant
                </h1>
            </header>

            <main ref={conversationRef} className="flex-1 p-4 md:p-6 overflow-y-auto">
                <div className="max-w-4xl mx-auto space-y-6">
                    {conversation.map((msg, index) => (
                        <ChatMessage key={index} role={msg.role} text={msg.text} />
                    ))}
                     {status !== 'idle' && (
                        <div className="flex justify-start">
                            <div className="max-w-xl lg:max-w-2xl">
                                <StatusIndicator />
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <footer className="p-4 bg-gray-900/70 backdrop-blur-sm border-t border-gray-700/50 flex-shrink-0">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center gap-2 bg-gray-800/80 border border-gray-600 rounded-full p-2 shadow-inner">
                        <input
                            type="text"
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleTextInputSubmit()}
                            placeholder="Type a message or use the mic..."
                            className="flex-1 bg-transparent focus:outline-none px-4 text-white placeholder-gray-400"
                            disabled={status !== 'idle'}
                        />
                        <button
                            onClick={handleTextInputSubmit}
                            className="p-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white transition-all duration-200 shadow-md disabled:bg-gray-500 disabled:cursor-not-allowed"
                            disabled={status !== 'idle' || !textInput.trim()}
                        >
                            <Send size={20} />
                        </button>
                        <button
                            onClick={startListening}
                            disabled={status !== 'idle' || !recognitionRef.current}
                            className={`p-2 rounded-full text-white transition-all duration-200 shadow-md ${
                                status === 'listening' ? 'bg-red-500 animate-pulse-glow' : 'bg-teal-500 hover:bg-teal-400'
                            } disabled:bg-gray-500 disabled:cursor-not-allowed`}
                        >
                            <Mic size={20} />
                        </button>
                    </div>
                    <div className="flex items-center justify-center mt-3">
                         <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-400">
                            <input 
                                type="checkbox" 
                                checked={isMuted} 
                                onChange={() => setIsMuted(!isMuted)}
                                className="appearance-none w-4 h-4 border-2 border-gray-500 rounded-md bg-gray-700 checked:bg-teal-500 checked:border-teal-400 transition duration-200"
                            />
                            Mute Jarvis
                        </label>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default JarvisAssistant;
